import { Box } from '@mui/system';
import React, { useState } from 'react';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { GaugeChart } from 'src/components/BeaconRedesignComponents/GaugeChart/GaugeChart';
import { useUpdateTimePeriods } from 'src/components/BeaconV2Components/common/TimePeriodDropdown';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton/SlSkeleton';
import { getIconForMetricChange } from 'src/components/BeaconV2Components/utils/chartStyles';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { useHistory, useMetricFormatter } from 'src/utils/Hooks';
import { useColorForPercentage } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/helpers/useColorForPercentage';
import {
  GaugeData,
  KeywordFilter,
  MetricFormatterProps,
  unClickableTiles
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/types';
import { useContentAnalyticStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/stores/contentAnalyticStore';
import colors from 'src/utils/colors';

export const CardContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex
      flexDirection="column"
      borderRadius="8px"
      paddingTop="24px"
      sx={{
        width: '336px',
        backgroundColor: colors.contentGenBackground,
        cursor: 'pointer'
      }}
    >
      {children}
    </Flex>
  );
};

interface GaugeSummaryCardProps {
  title: string;
  predefinedColor?: string;
  percentage: number;
}

const chartSize = 224;
const paneInnerRadius = 73;
const seriesInnerRadius = 82;
const seriesOuterRadius = 96;

export const GaugeSummaryCard = ({ title, predefinedColor, percentage }: GaugeSummaryCardProps) => {
  const formatMetric = useMetricFormatter();

  const formattedPercent = React.useMemo(
    () => formatMetric(percentage, METRICTYPE.PERCENT, { showFullValue: true, decimalPlaces: 1 }),
    [formatMetric, percentage]
  );

  const progressColor = useColorForPercentage(percentage);
  const gaugeColor = predefinedColor || progressColor;

  return (
    <>
      <Text sx={{ marginLeft: '24px' }} variant="h3">
        {title}
      </Text>
      <Flex justifyContent="center">
        <Box
          sx={{
            position: 'relative',
            borderRadius: '999px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1,
            marginTop: '12px',
            marginBottom: '4px'
          }}
        >
          <GaugeChart
            chartOptions={{
              chart: {
                backgroundColor: 'transparent',
                borderRadius: 999,
                borderWidth: 0,
                width: chartSize,
                height: chartSize,
                margin: 0,
                spacing: [0]
              },
              pane: {
                background: [
                  {
                    backgroundColor: '#FAFBFB'
                  },
                  {
                    backgroundColor: colors.graysSecondary
                  },
                  {
                    backgroundColor: '#FFFFFF',
                    innerRadius: `${paneInnerRadius}%`,
                    borderColor: 'transparent'
                  }
                ]
              },
              series: [
                {
                  type: 'solidgauge',
                  data: [
                    {
                      y: 1,
                      color: colors.graysSecondary,
                      radius: `${seriesOuterRadius}%`,
                      innerRadius: `${seriesInnerRadius}%`
                    },
                    {
                      y: percentage,
                      color: gaugeColor,
                      radius: `${seriesOuterRadius}%`,
                      innerRadius: `${seriesInnerRadius}%`
                    }
                  ]
                }
              ],
              title: {
                text: formattedPercent,
                style: {
                  color: colors.darkBlue,
                  fontFamily: 'Roboto',
                  fontSize: '32px',
                  fontWeight: '700',
                  lineHeight: '48px',
                  letterSpacing: '0.08px',
                  textAlign: 'center',
                  webkitFontSmoothing: 'antialiased'
                }
              },
              yAxis: {
                min: 0,
                max: 1
              }
            }}
          />
        </Box>
      </Flex>
    </>
  );
};

const CardDetailsContainer = ({
  children,
  isSelected,
  filterKey,
  height = '128px',
  onHover,
  overrideSx
}: {
  children: React.ReactNode;
  isSelected: boolean;
  filterKey: KeywordFilter;
  height?: string;
  onHover?: (isHovered: boolean) => void;
  overrideSx?: any;
}) => {
  const history = useHistory();
  const { setKeywordFilter } = useContentAnalyticStore();
  const availableTimePeriods = useAppSelector((state) => state.mainTimePeriod.availableMainTimePeriods);
  const updateTimePeriods = useUpdateTimePeriods({ history });

  return (
    <Flex
      flexDirection="column"
      sx={{
        height,
        gap: '4px',
        padding: '16px 24px',
        backgroundColor: isSelected ? colors.greyHover : 'inherit',
        cursor: unClickableTiles.includes(filterKey) ? 'default' : 'pointer',
        '&:hover': {
          backgroundColor: unClickableTiles.includes(filterKey) ? 'inherit' : colors.greyHover,
          transition: 'background-color 0.2s ease-in-out'
        },
        ...overrideSx
      }}
      onMouseEnter={() => onHover?.(true)}
      onMouseLeave={() => onHover?.(false)}
      onClick={() => {
        if (!unClickableTiles.includes(filterKey)) {
          setKeywordFilter(filterKey);
          updateTimePeriods(availableTimePeriods.find((period) => period.id === '1w')!);
        }
      }}
    >
      {children}
    </Flex>
  );
};

const CardDetailsChange = ({
  change,
  value,
  valueSuffix,
  valueMetricFormatter = {
    showFullValue: false,
    decimalPlaces: 0,
    valueMetricType: METRICTYPE.VOLUME
  }
}: {
  change?: number;
  value: number;
  valueSuffix?: string;
  valueMetricFormatter?: MetricFormatterProps;
}) => {
  const metricFormatter = useMetricFormatter();
  const { showFullValue, decimalPlaces, valueMetricType } = valueMetricFormatter;
  return (
    <Flex alignItems="center">
      <Text variant="subtitle1" sx={{ fontSize: '20px' }}>
        {metricFormatter(value, valueMetricType, { showFullValue, decimalPlaces })}
      </Text>
      {valueSuffix && (
        <Text variant="subtitle2" sx={{ marginLeft: '8px', fontSize: '12px' }}>
          {valueSuffix}
        </Text>
      )}

      {/* This is on purpose, we have 0, undefined, and non-zero values */}
      {change !== undefined && (
        <div
          style={{
            display: 'flex',
            marginLeft: '8px',
            alignItems: 'center',
            gap: '4px',
            fontSize: '12px',
            fontWeight: 500,
            marginTop: 'auto',
            marginBottom: 'auto'
          }}
        >
          <div style={{ marginTop: '3px' }}>{getIconForMetricChange(change, 1)}</div>
          <div style={{ fontSize: '12px', marginLeft: '2px', marginTop: '1px' }}>
            {metricFormatter(Math.abs(change), METRICTYPE.PERCENT, { showFullValue: true, decimalPlaces: 1 })}
          </div>
        </div>
      )}
    </Flex>
  );
};

export const CardDetails = ({
  title,
  subtitle,
  item,
  overrideSx
}: {
  title: string;
  subtitle: React.ReactNode;
  item: GaugeData;
  overrideSx?: any;
}) => {
  const { change, value, valueSuffix, filterKey, valueMetricFormatter } = item;
  const { keywordFilter } = useContentAnalyticStore();
  const [isHovered, setIsHovered] = useState(false);
  let height = '128px';
  if ([KeywordFilter.LowUsage, KeywordFilter.LowProductivity, KeywordFilter.LowPopularity].includes(filterKey)) {
    height = '108px';
  }
  const isSelected = keywordFilter === filterKey;

  const getTextColor = () => {
    if (isSelected) return 'inherit';
    if (isHovered && !unClickableTiles.includes(filterKey)) return colors.darkBlue;
    return colors.grey;
  };

  return (
    <CardDetailsContainer
      isSelected={isSelected}
      height={height}
      filterKey={filterKey}
      onHover={setIsHovered}
      overrideSx={overrideSx}
    >
      <Text
        variant="subtitle3"
        sx={{
          letterSpacing: '0.96px',
          height: '20px',
          WebkitFontSmoothing: 'antialiased',
          marginTop: '8px',
          color: getTextColor(),
          transition: 'color 0.2s ease-in-out'
        }}
      >
        {title}
      </Text>
      <CardDetailsChange
        change={change}
        value={value}
        valueSuffix={valueSuffix}
        valueMetricFormatter={valueMetricFormatter}
      />
      {subtitle}
    </CardDetailsContainer>
  );
};

export const GaugeSummaryCardSkeleton = () => {
  return <SlSkeleton variant="rounded" height="436px" width="336px" />;
};
